"use client";

import { type Dispatch, useEffect, useReducer, useRef, useState } from "react";
import { AlertCircle, ArrowRight, Play, RefreshCw, Rocket } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import {
  DEMO_FLOW_STAGE,
  type DemoFlowState,
  type DemoInput,
  type DemoPreview,
  type DemoTelemetrySnapshot,
} from "@/src/features/demo-contracts/types";
import type { GuidedDemoPersistedPreviewMap } from "@/src/features/demo-contracts/landing-preview-loader";
import {
  trackDemoError,
  trackResultShown,
  trackRunClicked,
} from "@/src/features/demo-contracts/analytics";
import {
  buildGuidedSamplePreviewForNiche,
  GUIDED_DEMO_PROGRESS_STEPS,
  GUIDED_DEMO_STARTER_NICHES,
} from "@/src/features/demo-contracts/sample-data";
import { DemoPreviewCard } from "./demo-preview";

/* -------------------------------------------------------------------------- */
/*  State machine                                                             */
/* -------------------------------------------------------------------------- */

type DemoAction =
  | { type: "SHOW_SAMPLE"; preview: DemoPreview }
  | { type: "START_RUN"; input: DemoInput }
  | {
      type: "UPDATE_TELEMETRY";
      telemetry: DemoTelemetrySnapshot;
      jobId?: string;
    }
  | {
      type: "COMPLETE";
      preview: DemoPreview;
      telemetry?: DemoTelemetrySnapshot;
    }
  | { type: "FAIL"; code: string; message: string; recoverable: boolean }
  | { type: "RESET" };

function demoReducer(state: DemoFlowState, action: DemoAction): DemoFlowState {
  switch (action.type) {
    case "SHOW_SAMPLE":
      return {
        stage: DEMO_FLOW_STAGE.SAMPLE_READY,
        preview: action.preview,
      };

    case "START_RUN": {
      if (
        state.stage !== DEMO_FLOW_STAGE.IDLE &&
        state.stage !== DEMO_FLOW_STAGE.SAMPLE_READY &&
        state.stage !== DEMO_FLOW_STAGE.ERROR
      ) {
        return state;
      }
      return {
        stage: DEMO_FLOW_STAGE.RUNNING,
        input: action.input,
      };
    }

    case "UPDATE_TELEMETRY": {
      if (state.stage !== DEMO_FLOW_STAGE.RUNNING) return state;
      return {
        ...state,
        jobId: action.jobId ?? state.jobId,
        telemetry: action.telemetry,
      };
    }

    case "COMPLETE": {
      if (state.stage !== DEMO_FLOW_STAGE.RUNNING) return state;
      return {
        stage: DEMO_FLOW_STAGE.LIVE_READY,
        input: state.input,
        preview: action.preview,
        telemetry: action.telemetry,
      };
    }

    case "FAIL": {
      const input: DemoInput =
        state.stage === DEMO_FLOW_STAGE.RUNNING
          ? state.input
          : { nicheSlug: "unknown", timeWindowDays: 30 };
      return {
        stage: DEMO_FLOW_STAGE.ERROR,
        input,
        code: action.code,
        message: action.message,
        recoverable: action.recoverable,
      };
    }

    case "RESET":
      return createIdleState();

    default:
      return state;
  }
}

function createIdleState(): DemoFlowState {
  return {
    stage: DEMO_FLOW_STAGE.IDLE,
    defaults: {
      nicheSlug: GUIDED_DEMO_STARTER_NICHES[0]?.slug ?? "ai-writing",
      timeWindowDays: 30,
    },
  };
}

/* -------------------------------------------------------------------------- */
/*  Stage-specific UI                                                         */
/* -------------------------------------------------------------------------- */

function PromptComposer({
  selectedNicheSlug,
  customNiche,
  showCustomRestriction,
  isRunning,
  onSelectNiche,
  onCustomNicheChange,
  onJoinWaitlist,
  onRun,
}: {
  selectedNicheSlug: string;
  customNiche: string;
  showCustomRestriction: boolean;
  isRunning: boolean;
  onSelectNiche: (slug: string) => void;
  onCustomNicheChange: (value: string) => void;
  onJoinWaitlist?: () => void;
  onRun: () => void;
}) {
  const canRun =
    selectedNicheSlug.trim().length > 0 || customNiche.trim().length > 0;

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-primary-foreground/45">
          Guided Prompt Box
        </p>
        <p className="text-sm font-semibold text-primary-foreground/90">
          Pick a niche to inspect demand signals before you build
        </p>
        <p className="max-w-xl text-xs leading-relaxed text-primary-foreground/60">
          Use a starter niche to generate a report preview, inspect details, and
          decide if deeper access is worth requesting for your next build cycle.
        </p>
      </div>

      <div className="grid w-full grid-cols-1 gap-2 sm:grid-cols-2">
        {GUIDED_DEMO_STARTER_NICHES.map((niche) => {
          const isActive = selectedNicheSlug === niche.slug;
          return (
            <button
              key={niche.slug}
              type="button"
              onClick={() => onSelectNiche(niche.slug)}
              disabled={isRunning}
              className={cn(
                "rounded-xl border px-3 py-2 text-left transition",
                isActive
                  ? "border-accent/70 bg-accent/15"
                  : "border-white/10 bg-white/5 hover:bg-white/10",
                isRunning ? "cursor-not-allowed opacity-60" : "",
              )}
              aria-pressed={isActive}
            >
              <p className="text-xs font-semibold text-primary-foreground/90">
                {niche.label}
              </p>
              <p className="mt-1 text-[10px] text-primary-foreground/50">
                {niche.helper}
              </p>
            </button>
          );
        })}
      </div>

      <div className="w-full text-left">
        <label
          htmlFor="demo-custom-niche"
          className="mb-1 block text-[11px] text-primary-foreground/60"
        >
          Or enter a custom niche
        </label>
        <Textarea
          id="demo-custom-niche"
          placeholder="e.g. B2B onboarding"
          rows={3}
          value={customNiche}
          onChange={(event) => onCustomNicheChange(event.target.value)}
          disabled={isRunning}
          className="min-h-24 resize-y border-white/15 bg-white/5 text-sm leading-relaxed text-primary-foreground placeholder:text-primary-foreground/35"
        />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Button
          size="lg"
          onClick={onRun}
          disabled={!canRun || isRunning}
          className="gap-2 bg-accent px-6 text-sm font-semibold text-accent-foreground shadow-lg shadow-accent/20 transition-all hover:bg-accent/90 hover:shadow-xl hover:shadow-accent/25"
        >
          <Play className="size-4" aria-hidden="true" />
          Inspect a niche
        </Button>
        <p className="text-[10px] text-primary-foreground/45">
          Deterministic MVP path. Takes around 30 seconds.
        </p>
      </div>

      {showCustomRestriction && (
        <div className="w-full rounded-xl border border-amber-300/40 bg-amber-300/10 p-3 text-left">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-amber-300">
            Custom niche — gated preview
          </p>
          <p className="mt-1.5 text-xs leading-relaxed text-primary-foreground/85">
            Custom niche analysis unlocks after you apply for access. We keep
            this path selective so the queue reflects stronger intent, not
            casual clicks.
          </p>
          <Button
            type="button"
            onClick={onJoinWaitlist}
            className="mt-3 h-9 bg-accent px-4 text-xs font-semibold text-accent-foreground hover:bg-accent/90"
          >
            Apply for access
          </Button>
        </div>
      )}
    </div>
  );
}

function IdleWorkspace() {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
      <p className="text-xs font-semibold text-primary-foreground/85">
        Your report will appear here
      </p>
      <p className="mt-2 text-[11px] leading-relaxed text-primary-foreground/55">
        Pick a niche above and hit{" "}
        <span className="text-primary-foreground/80">Inspect a niche</span> to
        inspect pain points, open details, and see your next step.
      </p>
    </div>
  );
}

function PostPreviewActions({
  preview,
  onJoinWaitlist,
}: {
  preview: DemoPreview;
  onJoinWaitlist?: () => void;
}) {
  return (
    <div className="rounded-xl border border-accent/25 bg-accent/5 p-4">
      <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-primary-foreground/50">
        Next Step
      </p>
      <p className="mt-2 text-xs text-primary-foreground/70">
        Keep exploring, then apply if you want the landing page prompt unlocked
        from this {preview.kind} preview.
      </p>
      <div className="mt-3 flex flex-col gap-2">
        <Button
          type="button"
          onClick={onJoinWaitlist}
          className="gap-2 bg-accent text-accent-foreground hover:bg-accent/90"
        >
          <Rocket className="size-4" aria-hidden="true" />
          Apply to unlock the landing page prompt
          <ArrowRight className="size-4" aria-hidden="true" />
        </Button>
      </div>
      <p className="mt-3 text-[10px] text-primary-foreground/45">
        Access stays selective on purpose. If approved, the prompt is built from
        your top pain signals and niche context.
      </p>
    </div>
  );
}

function RunningStage({ telemetry }: { telemetry?: DemoTelemetrySnapshot }) {
  const progress = telemetry?.progress ?? 0;
  const step = telemetry?.currentStep ?? "Collecting posts";

  const activeStepIndex = GUIDED_DEMO_PROGRESS_STEPS.findIndex(
    (item) => item.label === step,
  );

  return (
    <div
      className="flex flex-col items-center gap-4 py-8 text-center"
      aria-busy="true"
      aria-label="Generating report"
    >
      <Spinner className="size-8 text-accent" />
      <div className="space-y-2">
        <p className="text-sm font-semibold text-primary-foreground/90">
          Generating your report
        </p>
        <p className="text-xs text-primary-foreground/50" aria-live="polite">
          {step}
        </p>
      </div>
      {progress > 0 && (
        <div className="w-full max-w-xs space-y-1">
          <Progress
            value={progress}
            className="h-1.5"
            aria-label={`Report generation progress: ${progress}%`}
          />
          <p className="text-[10px] text-primary-foreground/40 text-right">
            {progress}%
          </p>
        </div>
      )}

      <div className="w-full rounded-xl border border-white/10 bg-white/[0.03] p-3 text-left">
        <p className="mb-2 text-[10px] uppercase tracking-wider text-primary-foreground/40">
          Pipeline Steps
        </p>
        <ul className="space-y-2">
          {GUIDED_DEMO_PROGRESS_STEPS.map((item, index) => {
            const isDone = progress >= item.progress;
            const isCurrent = activeStepIndex === index;

            return (
              <li key={item.id} className="flex items-start gap-2">
                <span
                  className={cn(
                    "mt-0.5 inline-flex size-4 items-center justify-center rounded-full border text-[9px] font-semibold",
                    isDone || isCurrent
                      ? "border-accent/70 bg-accent/20 text-accent"
                      : "border-white/20 text-primary-foreground/35",
                  )}
                >
                  {index + 1}
                </span>
                <div>
                  <p
                    className={cn(
                      "text-[11px]",
                      isDone || isCurrent
                        ? "text-primary-foreground/80"
                        : "text-primary-foreground/50",
                    )}
                  >
                    {item.label}
                  </p>
                  <p className="text-[10px] text-primary-foreground/40">
                    {item.helper}
                  </p>
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Loading skeleton hint — subtle, does not compete with progress */}
      <div className="w-full space-y-2 pt-1" aria-hidden="true">
        <Skeleton className="h-10 w-full rounded-xl bg-white/[0.03]" />
        <Skeleton className="h-8 w-3/4 rounded-xl bg-white/[0.03]" />
        <Skeleton className="h-6 w-1/2 rounded-xl bg-white/[0.03]" />
      </div>
    </div>
  );
}

function ErrorStage({
  message,
  recoverable,
  onRetry,
}: {
  message: string;
  recoverable: boolean;
  onRetry: () => void;
}) {
  return (
    <div className="flex flex-col items-center gap-4 py-8 text-center">
      <div className="mx-auto flex size-12 items-center justify-center rounded-full border border-red-400/30 bg-red-400/10">
        <AlertCircle className="size-6 text-red-400" aria-hidden="true" />
      </div>
      <div className="space-y-2">
        <p className="text-sm font-semibold text-primary-foreground/90">
          Something went wrong
        </p>
        <p className="mx-auto max-w-sm text-xs leading-relaxed text-primary-foreground/50">
          {message}
        </p>
      </div>
      {recoverable && (
        <Button
          variant="outline"
          size="lg"
          onClick={onRetry}
          className="gap-2 border-white/15 bg-white/5 text-sm text-primary-foreground/80 hover:bg-white/10"
        >
          <RefreshCw className="size-4" aria-hidden="true" />
          Try Again
        </Button>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Main component                                                            */
/* -------------------------------------------------------------------------- */

export interface DemoRunnerHandlers {
  /**
   * Called when the user triggers a run (sample or live).
   * The handler should eventually dispatch SHOW_SAMPLE or COMPLETE/FAIL
   * through the returned dispatch.
   */
  onRequestRun?: (
    input: DemoInput,
  ) => Promise<DemoPreview | void> | DemoPreview | void;
}

export interface DemoRunnerProps {
  /** Injected handlers to decouple from live API wiring. */
  handlers?: DemoRunnerHandlers;
  /** Pre-loaded sample preview to show immediately in sample_ready state. */
  initialPreview?: DemoPreview;
  /**
   * Persisted preview map keyed by nicheSlug.
   * When provided, the runner prefers persisted previews over seed builders.
   */
  persistedPreviewMap?: GuidedDemoPersistedPreviewMap;
  /** Optional callback when a preview becomes visible. */
  onPreviewVisible?: (preview: DemoPreview) => void;
  /** Optional callback for gated custom-niche waitlist CTA. */
  onRestrictedJoinWaitlist?: () => void;
  className?: string;
}

/**
 * Exposes the dispatch for parent or injected handlers to drive state
 * transitions externally (e.g., from API responses).
 */
export type DemoRunnerDispatch = Dispatch<DemoAction>;

export function DemoRunner({
  handlers,
  initialPreview,
  persistedPreviewMap,
  onPreviewVisible,
  onRestrictedJoinWaitlist,
  className,
}: DemoRunnerProps) {
  const initialState: DemoFlowState = createIdleState();

  const [state, dispatch] = useReducer(demoReducer, initialState);
  const containerRef = useRef<HTMLDivElement>(null);
  const prevStageRef = useRef(initialState.stage);
  const [selectedNicheSlug, setSelectedNicheSlug] = useState(
    GUIDED_DEMO_STARTER_NICHES[0]?.slug ?? "ai-writing",
  );
  const [customNiche, setCustomNiche] = useState("");
  const [showCustomRestriction, setShowCustomRestriction] = useState(false);

  function resolveNicheSelection(): string {
    const trimmedCustom = customNiche.trim();
    if (trimmedCustom.length > 0) {
      return trimmedCustom;
    }
    return selectedNicheSlug;
  }

  async function runSampleProgress(input: DemoInput) {
    for (const [index, stepItem] of GUIDED_DEMO_PROGRESS_STEPS.entries()) {
      await new Promise<void>((resolve) => {
        setTimeout(resolve, index === 0 ? 350 : 450);
      });

      dispatch({
        type: "UPDATE_TELEMETRY",
        telemetry: {
          progress: stepItem.progress,
          currentStep: stepItem.label,
        },
      });
    }

    // Phase 2: prefer persisted preview when available
    const persisted = persistedPreviewMap?.[input.nicheSlug];
    const preview =
      persisted ??
      buildGuidedSamplePreviewForNiche(input.nicheSlug, initialPreview);

    dispatch({ type: "SHOW_SAMPLE", preview });
    trackResultShown(preview.nicheSlug, preview.kind);
  }

  /* ---- Focus management on stage transitions ---- */
  useEffect(() => {
    if (state.stage !== prevStageRef.current) {
      prevStageRef.current = state.stage;
      // Move focus to the container on meaningful transitions so screen
      // readers announce the new content via aria-live.
      containerRef.current?.focus({ preventScroll: true });
    }
  }, [state.stage]);

  /* ---- Callbacks ---- */

  async function handleRun() {
    const trimmedCustomNiche = customNiche.trim();
    if (trimmedCustomNiche.length > 0) {
      setShowCustomRestriction(true);
      return;
    }

    const nicheSlug = resolveNicheSelection();
    const input: DemoInput =
      state.stage === DEMO_FLOW_STAGE.IDLE
        ? { ...state.defaults, nicheSlug }
        : { nicheSlug, timeWindowDays: 30 };

    // Analytics: track run click
    trackRunClicked(input.nicheSlug, input.timeWindowDays);

    if (handlers?.onRequestRun) {
      dispatch({ type: "START_RUN", input });
      try {
        const preview = await handlers.onRequestRun(input);

        if (preview) {
          dispatch({ type: "COMPLETE", preview });
          // Analytics: track result shown
          trackResultShown(input.nicheSlug, preview.kind);
        }
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : "An unexpected error occurred.";
        dispatch({
          type: "FAIL",
          code: "handler_error",
          message,
          recoverable: true,
        });
        // Analytics: track error
        trackDemoError(input.nicheSlug, "handler_error");
      }
    } else {
      dispatch({ type: "START_RUN", input });
      await runSampleProgress(input);
    }
  }

  function handleRetry() {
    if (state.stage === DEMO_FLOW_STAGE.ERROR) {
      void handleRun();
    }
  }

  function handleCustomNicheChange(value: string) {
    setCustomNiche(value);
    if (value.trim().length === 0) {
      setShowCustomRestriction(false);
    }
  }

  /* ---- Notify parent when preview becomes visible ---- */

  const previewFromState =
    state.stage === DEMO_FLOW_STAGE.SAMPLE_READY
      ? state.preview
      : state.stage === DEMO_FLOW_STAGE.LIVE_READY
        ? state.preview
        : null;

  useEffect(() => {
    if (previewFromState) {
      onPreviewVisible?.(previewFromState);
    }
  }, [onPreviewVisible, previewFromState]);

  /* ---- Derived state ---- */
  const isRunning = state.stage === DEMO_FLOW_STAGE.RUNNING;

  /* ---- Render ---- */

  return (
    <div
      ref={containerRef}
      className={cn(
        "w-full rounded-[calc(1.75rem-2px)] border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.07),rgba(255,255,255,0.03))] shadow-2xl backdrop-blur-sm",
        className,
      )}
      role="region"
      aria-label="Demo runner"
      aria-live="polite"
      aria-busy={isRunning}
      tabIndex={-1}
    >
      <div className="border-b border-white/10 bg-black/10 p-4 sm:p-5">
        <PromptComposer
          selectedNicheSlug={selectedNicheSlug}
          customNiche={customNiche}
          showCustomRestriction={showCustomRestriction}
          isRunning={isRunning}
          onSelectNiche={setSelectedNicheSlug}
          onCustomNicheChange={handleCustomNicheChange}
          onJoinWaitlist={onRestrictedJoinWaitlist}
          onRun={handleRun}
        />
      </div>

      {/* Nested border cleanup: remove redundant inner border in sample_ready/live_ready */}
      <div className="space-y-4 p-4 sm:p-5">
        {state.stage === DEMO_FLOW_STAGE.IDLE && <IdleWorkspace />}

        {state.stage === DEMO_FLOW_STAGE.RUNNING && (
          <RunningStage telemetry={state.telemetry} />
        )}

        {state.stage === DEMO_FLOW_STAGE.ERROR && (
          <ErrorStage
            message={state.message}
            recoverable={state.recoverable}
            onRetry={handleRetry}
          />
        )}

        {previewFromState && (
          <DemoPreviewCard preview={previewFromState} surface="embedded" />
        )}

        {previewFromState && (
          <PostPreviewActions
            preview={previewFromState}
            onJoinWaitlist={onRestrictedJoinWaitlist}
          />
        )}
      </div>
    </div>
  );
}

/**
 * Re-export the action type so parents can build actions without
 * importing from this module's internals.
 */
export type { DemoAction };
